import React, { useEffect, useState, useRef } from "react";
import {
  Grid,
  Typography,
  makeStyles,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  CircularProgress,
} from "@material-ui/core";
import Pagination from "@material-ui/lab/Pagination";
import moment from "moment";
import { useForm, Controller } from "react-hook-form";
import { useQuery, useMutation, useLazyQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";

import { Card, CardContent, CardActions } from "../../components/Card";
import Table from "../../components/Table";
import Button from "../../components/Button";
import { useAlert } from "../../components/Alert";
import { DateInput, Select, TextInput } from "../../components/Form";
import Text from "../../components/Text";
import LoadingModal from "../../components/LoadingModal";

const GET_FORECLOSURES = gql`
  query foreclosures(
    $obj_no: [Int]
    $obj_court: String
    $obj_address: String
    $obj_date_ymd_from: Date
    $obj_date_ymd_to: Date
    $desc: Boolean
    $pageSize: Int
    $page: Int
  ) {
    foreclosures(
      obj_no: $obj_no
      obj_court: $obj_court
      obj_address: $obj_address
      obj_date_ymd_from: $obj_date_ymd_from
      obj_date_ymd_to: $obj_date_ymd_to
      desc: $desc
      pageSize: $pageSize
      page: $page
    ) {
      count
      pageCount
      contents {
        obj_no
        obj_str
        obj_share
        obj_caseno
        obj_another
        obj_time
        obj_date_ymd
        obj_date_hi
        obj_city
        obj_area
        obj_section
        obj_section_s
        obj_landno
        obj_address
        obj_link
        obj_margin
        obj_price
        obj_price_set
        obj_creditor
        obj_debtor
        obj_bulletin_data
        obj_remark
        obj_land_data
        obj_build_data
        obj_land_floor
        obj_build_floor
        obj_add_floor
        obj_s_floor
        obj_c_floor
        obj_total_floor
        obj_transfer
        obj_type
        obj_creat_date
        obj_stop
        obj_tender_num
        obj_tender
        obj_check_num
        obj_marker
        obj_request_num
        obj_cadastral
        obj_patterns
      }
    }
  }
`;

const GET_TARGETPROPERTIES = gql`
  query targetProperties(
    $id: Int!
    $latest: Boolean
    $pageSize: Int
    $page: Int
  ) {
    targetProperties(
      id: $id
      latest: $latest
      pageSize: $pageSize
      page: $page
    ) {
      pageCount
      contents {
        id
        foreclosure {
          obj_no
          obj_str
          obj_share
          obj_caseno
          obj_another
          obj_time
          obj_date_ymd
          obj_date_hi
          obj_city
          obj_area
          obj_section
          obj_section_s
          obj_landno
          obj_address
          obj_link
          obj_margin
          obj_price
          obj_price_set
          obj_creditor
          obj_debtor
          obj_bulletin_data
          obj_remark
          obj_land_data
          obj_build_data
          obj_land_floor
          obj_build_floor
          obj_add_floor
          obj_s_floor
          obj_c_floor
          obj_total_floor
          obj_transfer
          obj_type
          obj_creat_date
          obj_stop
          obj_tender_num
          obj_tender
          obj_check_num
          obj_marker
          obj_request_num
          obj_cadastral
          obj_patterns
        }
      }
    }
  }
`;

const SAVE_TARGETPROPERTY = gql`
  mutation saveTargetProperty($userId: Int!, $obj_no: [Int]!) {
    saveTargetProperty(userId: $userId, obj_no: $obj_no)
  }
`;

const DELETE_TARGETPROPERTY = gql`
  mutation deleteTargetProperty($ids: [Int]!) {
    deleteTargetProperty(ids: $ids)
  }
`;

export default function TargetPropertyTable({ id }) {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [ids, setIds] = useState([]);
  const useStyles = makeStyles({
    DocumentContainerButtonEnd: {
      display: "flex",
      justifyContent: "flex-end",
    },
    DocumentContainerBetween: {
      alignItems: "center",
      //flexDirection: "row",
      justifyContent: "space-between",
    },
  });
  const classes = useStyles();
  const Alert = useAlert();
  const [getTargetProperties, { data, loading }] = useLazyQuery(
    GET_TARGETPROPERTIES,
    {
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
      onError(error) {
        Alert.alert("通知", `${error}`, [
          {
            text: "確定",
            type: "ok",
          },
        ]);
      },
    }
  );
  const [deleteTargetProperty, { loading: deleteTargetPropertyLoading }] =
    useMutation(DELETE_TARGETPROPERTY, {
      onCompleted({ deleteTargetProperty }) {
        if (deleteTargetProperty) {
          Alert.alert("", "刪除成功", [
            {
              text: "確定",
              onPress: getTargetProperties({
                variables: { id, latest: true, pageSize: 5, page },
              }),
              type: "ok",
            },
          ]);
        } else {
          Alert.alert("", "刪除失敗，請再次嘗試。", [
            { text: "確定", type: "ok" },
          ]);
        }
      },
      onError(error) {
        Alert.notice(`${error.message.replace("GraphQL error: ", "")}`);
      },
    });
  useEffect(() => {
    if (id) {
      getTargetProperties({
        variables: { id, latest: true, pageSize: 5, page },
      });
    }
  }, [id, page]);
  if (loading) {
    return (
      <Grid container justify="center">
        <CircularProgress color="secondary" />
      </Grid>
    );
  } else {
    return (
      <>
        <LoadingModal loading={deleteTargetPropertyLoading} />
        <CardContent>
          <Grid
            container
            spacing={1}
            classes={{ root: classes.DocumentContainerBetween }}
          >
            <Typography style={{ fontSize: 25 }}>物件鎖定</Typography>
            <Grid
              xs={12}
              sm={4}
              item
              classes={{ root: classes.DocumentContainerButtonEnd }}
            >
              <Button
                size="small"
                color="primary"
                variant="contained"
                fullWidth
                onPress={() => {
                  setOpen(true);
                }}
              >
                新增物件鎖定
              </Button>
              <TargetPropertyDialog
                id={id}
                open={open}
                onClose={() => {
                  setOpen(false);
                }}
              />
              {"\u2002"}
              <Button
                size="small"
                color="primary"
                variant="contained"
                fullWidth
                onPress={() => {
                  deleteTargetProperty({ variables: { ids } });
                }}
                disabled={ids.length == 0}
              >
                移除物件鎖定
              </Button>
            </Grid>
          </Grid>
        </CardContent>
        <Divider />
        <CardContent>
          <Table
            data={data?.targetProperties.contents}
            tableWidth={2}
            header={[
              "",
              "編號",
              "案號\n債務人",
              "大樓案名\n門牌地址",
              "日期\n時間",
              "類別\n狀況",
              "建坪\n地坪",
              "建坪／萬\n地坪／萬",
              "拍賣底價\n保證金",
              "拍次\n點交",
            ]}
            columns={[
              (item) => (
                <Checkbox
                  checked={Boolean(ids.find((item2) => item2 === item.id))}
                  onClick={(event) => {
                    event.stopPropagation();
                    const hasId = ids.find((item2) => item2 === item.id);
                    if (hasId) setIds((e) => e.filter((i) => i !== item.id));
                    else setIds((e) => [...e, Number(item.id)]);
                  }}
                />
              ),
              (item) => (
                <Grid>
                  <Text>{item.foreclosure.obj_no}</Text>
                </Grid>
              ),
              (item) => (
                <>
                  <Text color="#22b169">
                    {item.foreclosure.obj_str +
                      item.foreclosure.obj_share +
                      item.foreclosure.obj_caseno +
                      "\n"}
                  </Text>
                  <Text>{item.foreclosure.obj_debtor}</Text>
                </>
              ),
              (item) => (
                <>
                  <Text color="#22b169">
                    {item.foreclosure.obj_city +
                      item.foreclosure.obj_area +
                      item.foreclosure.obj_section +
                      "\n"}
                  </Text>
                  <Text>
                    {item.foreclosure.obj_address
                      ? item.foreclosure.obj_address
                      : ""}
                  </Text>
                </>
              ),
              (item) => (
                <>
                  <Text color="#22b169">
                    {moment(item.foreclosure.obj_date_ymd).format(
                      "YYYY-MM-DD"
                    ) + "\n"}
                  </Text>
                  <Text>
                    {moment(item.foreclosure.obj_date_hi).format("HH:mm")}
                  </Text>
                </>
              ),
              (item) =>
                item.foreclosure.obj_type === "L"
                  ? item.foreclosure.obj_patterns
                    ? item.foreclosure.obj_patterns
                    : "土地"
                  : item.foreclosure.obj_patterns,
              (item) => (
                <>
                  <Text color="#22b169">
                    {item.foreclosure.obj_build_floor + "\n"}
                  </Text>
                  <Text>{item.foreclosure.obj_land_floor}</Text>
                </>
              ),
              (item) => (
                <>
                  <Text color="#22b169">
                    {item.foreclosure.obj_land_floor === 0
                      ? 0
                      : (
                          item.foreclosure.obj_price /
                          item.foreclosure.obj_land_floor
                        ).toFixed(2) +
                        "萬" +
                        "\n"}
                  </Text>
                  <Text>
                    {item.foreclosure.obj_total_floor === 0
                      ? 0
                      : (
                          item.foreclosure.obj_price /
                          item.foreclosure.obj_total_floor
                        ).toFixed(2) + "萬"}
                  </Text>
                </>
              ),
              (item) => (
                <>
                  <Text color="#22b169">
                    {item.foreclosure.obj_price + "萬" + "\n"}
                  </Text>
                  <Text>
                    {(item.foreclosure.obj_margin / 10000).toFixed(2) + "萬"}
                  </Text>
                </>
              ),
              (item) => (
                <>
                  <Text color="#22b169">
                    {item.foreclosure.obj_time + "\n"}
                  </Text>
                  <Text>
                    {(() => {
                      switch (item.foreclosure.obj_transfer) {
                        case "0":
                          return "不點交";
                        case "1":
                          return "點交";
                        case "2":
                          return item.foreclosure.obj_remark;
                        default:
                          break;
                      }
                    })()}
                  </Text>
                </>
              ),
            ]}
          />
        </CardContent>
        <CardActions>
          <Grid container justifyContent="center">
            <Pagination
              count={
                data?.targetProperties.pageCount
                  ? data.targetProperties.pageCount
                  : 1
              }
              color="primary"
              size="large"
              page={page}
              onChange={(e, n) => setPage(n)}
            />
          </Grid>
        </CardActions>
      </>
    );
  }
}

function TargetPropertyDialog({ id, open, onClose = () => {} }) {
  const courtArray = [
    {
      label: "臺灣台北地方法院",
      value: "TPD",
    },
    {
      label: "臺灣新北地方法院",
      value: "PCD",
    },
    {
      label: "臺灣士林地方法院",
      value: "SLD",
    },
    {
      label: "臺灣桃園地方法院",
      value: "TYD",
    },
    {
      label: "臺灣新竹地方法院",
      value: "SCD",
    },
    {
      label: "臺灣苗栗地方法院",
      value: "MLD",
    },
    {
      label: "臺灣臺中地方法院",
      value: "TCD",
    },
    {
      label: "臺灣南投地方法院",
      value: "NTD",
    },
    {
      label: "臺灣彰化地方法院",
      value: "CHD",
    },
    {
      label: "臺灣雲林地方法院",
      value: "ULD",
    },
    {
      label: "臺灣嘉義地方法院",
      value: "CYD",
    },
    {
      label: "臺灣臺南地方法院",
      value: "TND",
    },
    {
      label: "臺灣橋頭地方法院",
      value: "CTD",
    },
    {
      label: "臺灣高雄地方法院",
      value: "KSD",
    },
    {
      label: "臺灣屏東地方法院",
      value: "PTD",
    },
    {
      label: "臺灣臺東地方法院",
      value: "TTD",
    },
    {
      label: "臺灣花蓮地方法院",
      value: "HLD",
    },
    {
      label: "臺灣宜蘭地方法院",
      value: "ILD",
    },
    {
      label: "臺灣基隆地方法院",
      value: "KLD",
    },
    {
      label: "臺灣澎湖地方法院",
      value: "PHD",
    },
    {
      label: "福建金門地方法院",
      value: "KMD",
    },
    {
      label: "福建連江地方法院",
      value: "LCD",
    },
  ];
  const searchStatus = useRef(false);
  const [ids, setIds] = useState([]);
  const { control, handleSubmit, setValue, getValues } = useForm({
    defaultValues: {
      courtObject: {},
      obj_no: null,
      obj_court: "",
      obj_address: "",
      obj_date_ymd_from: new Date(),
      obj_date_ymd_to: moment(new Date()).add(3, "month"),
      page: 1,
    },
  });
  const Alert = useAlert();
  const useStyles = makeStyles({
    rangeDatePseudo: {
      position: "relative",
      "&::before": {
        content: "'-'",
        position: "absolute",
        top: "50%",
        left: "-4px",
        transform: "translate(2px,-50%)",
      },
    },
  });
  const classes = useStyles();
  const [getForeclosures, { data: foreclosuresData, loading }] = useLazyQuery(
    GET_FORECLOSURES,
    {
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
      onError(error) {
        Alert.alert("通知", `${error}`, [
          {
            text: "確定",
            type: "ok",
          },
        ]);
      },
    }
  );

  const [saveTargetProperty, { loading: saveTrackRecordLoading }] = useMutation(
    SAVE_TARGETPROPERTY,
    {
      onCompleted({ saveTargetProperty }) {
        if (saveTargetProperty) {
          Alert.alert("", "儲存成功", [
            {
              text: "確定",
              onPress: () => {
                onClose("refetch");
              },
              type: "ok",
            },
          ]);
        } else {
          Alert.alert("", "儲存失敗，請再次嘗試。", [
            { text: "確定", type: "ok" },
          ]);
        }
      },
      onError(error) {
        Alert.notice(`${error.message.replace("GraphQL error: ", "")}`);
      },
    }
  );
  function search(data) {
    searchStatus.current = true;
    getForeclosures({
      variables: {
        obj_no: data.obj_no,
        // obj_court: data.obj_court,
        // obj_address: data.obj_address,
        // obj_date_ymd_from: data.obj_date_ymd_from,
        // obj_date_ymd_to: data.obj_date_ymd_to,
        desc: true,
        pageSize: 10,
        page: getValues("page"),
      },
    });
  }
  function save() {
    saveTargetProperty({ variables: { userId: id, obj_no: ids } });
  }

  useEffect(() => {
    if (searchStatus.current) {
      getForeclosures({
        variables: {
          obj_no: getValues("obj_no"),
          obj_court: getValues("obj_court"),
          obj_address: getValues("obj_address"),
          obj_date_ymd_from: getValues("obj_date_ymd_from"),
          obj_date_ymd_to: getValues("obj_date_ymd_to"),
          desc: true,
          pageSize: 10,
          page: getValues("page"),
        },
      });
    }
  }, [getValues("page")]);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={searchStatus.current ? "lg" : "sm"}
    >
      <LoadingModal loading={loading || saveTrackRecordLoading} />
      <DialogTitle align="center">搜尋物件</DialogTitle>
      <DialogContent>
        {searchStatus.current ? (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Table
                data={foreclosuresData?.foreclosures.contents}
                tableWidth={2}
                header={[
                  "",
                  "編號",
                  "案號\n債務人",
                  "大樓案名\n門牌地址",
                  "日期\n時間",
                  "類別\n狀況",
                  "建坪\n地坪",
                  "建坪／萬\n地坪／萬",
                  "拍賣底價\n保證金",
                  "拍次\n點交",
                ]}
                columns={[
                  (item) => (
                    <Checkbox
                      checked={Boolean(
                        ids.find((item2) => item2 === item.obj_no)
                      )}
                      onClick={(event) => {
                        event.stopPropagation();
                        const hasId = ids.find(
                          (item2) => item2 === item.obj_no
                        );
                        if (hasId)
                          setIds((e) => e.filter((i) => i !== item.obj_no));
                        else setIds((e) => [...e, Number(item.obj_no)]);
                      }}
                    />
                  ),
                  (item) => (
                    <Grid>
                      <Text>{item.obj_no}</Text>
                    </Grid>
                  ),
                  (item) => (
                    <>
                      <Text color="#22b169">
                        {item.obj_str + item.obj_share + item.obj_caseno + "\n"}
                      </Text>
                      <Text>{item.obj_debtor}</Text>
                    </>
                  ),
                  (item) => (
                    <>
                      <Text color="#22b169">
                        {item.obj_city +
                          item.obj_area +
                          item.obj_section +
                          "\n"}
                      </Text>
                      <Text>{item.obj_address ? item.obj_address : ""}</Text>
                    </>
                  ),
                  (item) => (
                    <>
                      <Text color="#22b169">
                        {moment(item.obj_date_ymd).format("YYYY-MM-DD") + "\n"}
                      </Text>
                      <Text>{moment(item.obj_date_hi).format("HH:mm")}</Text>
                    </>
                  ),
                  (item) =>
                    item.obj_type === "L"
                      ? item.obj_patterns
                        ? item.obj_patterns
                        : "土地"
                      : item.obj_patterns,
                  ,
                  (item) => (
                    <>
                      <Text color="#22b169">{item.obj_build_floor + "\n"}</Text>
                      <Text>{item.obj_land_floor}</Text>
                    </>
                  ),
                  (item) => (
                    <>
                      <Text color="#22b169">
                        {item.obj_land_floor === 0
                          ? 0
                          : (item.obj_price / item.obj_land_floor).toFixed(2) +
                            "萬" +
                            "\n"}
                      </Text>
                      <Text>
                        {item.obj_total_floor === 0
                          ? 0
                          : (item.obj_price / item.obj_total_floor).toFixed(2) +
                            "萬"}
                      </Text>
                    </>
                  ),
                  (item) => (
                    <>
                      <Text color="#22b169">
                        {item.obj_price + "萬" + "\n"}
                      </Text>
                      <Text>{(item.obj_margin / 10000).toFixed(2) + "萬"}</Text>
                    </>
                  ),
                  (item) => (
                    <>
                      <Text color="#22b169">{item.obj_time + "\n"}</Text>
                      <Text>
                        {(() => {
                          switch (item.obj_transfer) {
                            case "0":
                              return "不點交";
                            case "1":
                              return "點交";
                            case "2":
                              return item.obj_remark;
                            default:
                              break;
                          }
                        })()}
                      </Text>
                    </>
                  ),
                ]}
              />
            </Grid>
            <Grid item container justifyContent="center">
              <Pagination
                count={1}
                color="primary"
                size="large"
                page={getValues("page")}
                onChange={(e, n) => setValue("page", n)}
              />
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={1}>
            <Grid item sm={6} xs={6}>
              <Typography color="primary">選擇法院</Typography>
              <Controller
                name="courtObject"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Select
                    items={courtArray}
                    {...field}
                    onChange={(e, v) => {
                      setValue("courtObject", v);
                      setValue("obj_court", v.value);
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item sm={6} xs={6}>
              <Typography color="primary">案號</Typography>
              <Controller
                name="obj_no"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField {...field} fullWidth />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography color="primary">投標日</Typography>
              <Grid
                container
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
              >
                <Grid item xs={6}>
                  <Controller
                    name="obj_date_ymd_from"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <DateInput {...field} format={"yyyy-MM-dd"} fullWidth />
                    )}
                  />
                </Grid>
                <Grid item xs={6} className={classes.rangeDatePseudo}>
                  <Controller
                    name="obj_date_ymd_to"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <DateInput {...field} format={"yyyy-MM-dd"} fullWidth />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item sm={12} xs={12}>
              <Typography color="primary">地址/地段</Typography>
              <Controller
                name="obj_address"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField {...field} fullWidth />
                )}
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Grid container spacing={1} justifyContent="center">
          <Grid item>
            <Button
              color="primary"
              variant="contained"
              onPress={searchStatus.current ? save : handleSubmit(search)}
            >
              {searchStatus.current ? "新增" : "搜尋"}
            </Button>
          </Grid>
          <Grid item>
            <Button color="primary" variant="contained" onPress={onClose}>
              返回
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
}
